import { observer } from "mobx-react-lite";
import React, { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  Container,
  Form,
  Grid,
  Label,
  Message,
  Table,
} from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import { Formik, FormikErrors } from "formik";
import * as Yup from "yup";
import { Link } from "react-router-dom";
import { taxOptions } from "../../../app/common/options/option";
import PageHeader from "../../../app/layout/PageHeader";
import { useStore } from "../../../app/stores/store";
import {
  SettingsPricingTaxRateFormValues,
  SettingsTaxRatesDto,
} from "../../../app/models/pricing";
import MyIconTextInput from "../../../app/common/form/MyIconTextInput";
import { RegexConstants } from "../../../app/common/util/regexConstants";
import LoadingComponent from "../../../app/layout/LoadingComponent";
import MyHelpButton from "../../../app/common/form/MyHelpButton";
import { Application, SalesTaxOptionType } from "../../../app/models/enums";
import ConfirmDelete from "../../../app/common/form/ConfirmDelete";
import MyPaging from "../../../app/common/form/MyPaging";
import { PagingParams } from "../../../app/models/pagination";
import { salesTaxOptionChecked, salesTaxOptionToString } from "../../../app/common/util/functions";
import UpsertTaxRate from "./UpsertTaxRate";

export default observer(function SalesTax() {
  const { t } = useTranslation(["common", "pricingSettings", "errors"]);
  const { multipleTaxRatesStore, userStore, modalStore, pricingStore } =
    useStore();
  const {
    getTaxRates,
    loadMultipleTaxRates,
    removeSettingsTaxRate,
    setPagingParams,
    loadingMultipleTaxes: loadingInitial,
    pagination,
    pagingParams,
    setSelectedSettingsTaxRate,
  } = multipleTaxRatesStore;
  const {
    loadSettingsPricingTaxRate,
    createSettingsPricingTaxRate,
    updateSettingsPricingTaxRate,
    selectedSettingsPricingTaxRate,
  } = pricingStore;
  const { checkApplication } = userStore;

  const decimalExp = new RegExp(RegexConstants.decimalExp);
  const validationSchema = Yup.object({
    taxRate: Yup.string()
      .required(t("pricing.required_taxrate", { ns: "errors" }))
      .matches(decimalExp, t("pricing.invalid_taxrate", { ns: "errors" }))
      .matches(
        new RegExp(RegexConstants.threeDecimalPlacesRegex),
        t("pricing.taxrate_length", { ns: "errors" })
      ),
    saleTaxOption: Yup.string().required(),
  });

  const [settingsTaxRate, setSettingsTaxRate] =
    useState<SettingsPricingTaxRateFormValues>(
      new SettingsPricingTaxRateFormValues(selectedSettingsPricingTaxRate)
    );

  const [completed, setCompleted] = useState<boolean>(false);

  useEffect(() => {
    loadMultipleTaxRates();
  }, [loadMultipleTaxRates]);

  useEffect(() => {
    loadSettingsPricingValues();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadSettingsPricingTaxRate]);

  function loadSettingsPricingValues() {
    loadSettingsPricingTaxRate().then((taxRate) =>
      setSettingsTaxRate(new SettingsPricingTaxRateFormValues(taxRate))
    );
  }
  function handleFormSubmit(
    values: SettingsPricingTaxRateFormValues,
    setErrors: (errors: FormikErrors<SettingsPricingTaxRateFormValues>) => void,
    setSubmitting: (isSubmitting: boolean) => void
  ) {
    const action = values.id
      ? () => updateSettingsPricingTaxRate(values)
      : () => createSettingsPricingTaxRate(values);

    action()
      .then(() => {
        loadSettingsPricingValues();
        setSubmitting(false);
        setCompleted(true);
      })
      .catch((error) => {
        setSubmitting(false);
        setErrors({ error: error });
      });
  }
  function handleDelete(settingsTaxRateId: string) {
    removeSettingsTaxRate(settingsTaxRateId)
      .then(() => {
        loadMultipleTaxRates();
      })
      .catch((error: any) => {
        console.log("Remove comment error:", error);
      });
  }

  function listOnClick(value: number) {
    if (value > 0 && pagination && value < pagination?.totalPages + 1) {
      setPagingParams(new PagingParams(value));
      loadMultipleTaxRates();
    }
  }

  if (loadingInitial)
    return (
      <LoadingComponent
        content={t("salesTax_loading", { ns: "pricingSettings" })}
      />
    );

  return (
    <>
      <PageHeader
        header={t("salestax", { ns: "common" })}
        type={"h2"}
        divider={true}
        getAlerts={true}
        addTitle={true}
      />

      <Container className="page">
        <Formik
          validationSchema={validationSchema}
          enableReinitialize
          initialValues={settingsTaxRate}
          onSubmit={(values, { setErrors, setSubmitting }) =>
            handleFormSubmit(values, setErrors, setSubmitting)
          }
        >
          {({
            handleSubmit,
            isValid,
            isSubmitting,
            dirty,
            handleChange,
            handleBlur,
            errors,
            values,
            touched,
          }) => (
            <Form
              className="ui form error"
              autoComplete="off"
              onSubmit={handleSubmit}
            >
              {completed && isValid && (
                <Message positive>
                  {t("settingsupdated", {
                    ns: "common",
                  })}
                </Message>
              )}
              <Grid stackable>
                <>
                  <Grid.Row>
                    <Grid.Column width={"7"}>
                       <label className="mylabel required">
                        {`${t("tax.rate", { ns: "pricingSettings" })}:`}
                      </label>
                      <MyHelpButton
                        name="saleTaxOptionHelp"
                        position="bottom left"
                        help="settings.pricing.salesTaxRate"
                        color={"blue"}
                      />
                      <MyIconTextInput
                        name="taxRate"
                        placeholder={t("tax.rate", { ns: "pricingSettings" })}
                        icon="percent"
                        className="drop-down"
                        maxLength={500}
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column>
                      <label className="mylabel required">
                        {`${t("tax.apply2", { ns: "pricingSettings" })}:`}
                      </label>
                      <MyHelpButton
                        name="saleTaxOptionHelp"
                        position="bottom left"
                        help="settings.pricing.salesTaxOptions"
                        color={"blue"}
                        seeMore={true}
                      />
                      <Form.Group grouped>
                        {taxOptions.map((data, index) => {
                          //console.log(data, index);
                          return (
                            <Form.Field key={`saleTaxOption[${data.value}]`}>
                              <Checkbox
                                id={`saleTaxOption-${index}`}
                                key={`saleTaxOption-${data.value}`}
                                value={data.value}
                                name={`saleTaxOption-${data.value}`}
                                onChange={(e, { checked }) => {
                                  const truthy =
                                    data.value === SalesTaxOptionType.ExemptMisc
                                      ? !checked
                                      : checked;
                                  const newValue = truthy
                                    ? values.saleTaxOption |
                                      (data.value as number)
                                    : values.saleTaxOption & ~data.value;

                                  if (
                                    data.value ===
                                      SalesTaxOptionType.OnTicket &&
                                    checked
                                  ) {
                                    // If "onticket" is checked, also check "parts" and "labor"
                                    values.saleTaxOption =
                                      newValue |
                                      SalesTaxOptionType.Parts |
                                      SalesTaxOptionType.Labor;
                                  } else if (
                                    (data.value === SalesTaxOptionType.Parts ||
                                      data.value ===
                                        SalesTaxOptionType.Labor) &&
                                    !checked
                                  ) {
                                    // If requirements of "onticket" are unchecked, uncheck "onticket"
                                    values.saleTaxOption =
                                      newValue & ~SalesTaxOptionType.OnTicket;
                                  } else {
                                    values.saleTaxOption = newValue;
                                  }

                                  handleChange(e);
                                }}
                                onBlur={handleBlur}
                                label={data.text}
                                checked={salesTaxOptionChecked(
                                  values.saleTaxOption,
                                  data.value as number
                                )}
                              />
                            </Form.Field>
                          );
                        })}
                      </Form.Group>

                      {touched.saleTaxOption && errors.saleTaxOption && (
                        <Label basic color="red">
                          {errors.saleTaxOption}
                        </Label>
                      )}
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column>
                      <Button
                        color="green"
                        className="save_button"
                        icon="check"
                        content={t("save", { ns: "common" })}
                        disabled={isSubmitting || !dirty || !isValid}
                        loading={isSubmitting}
                        type="submit"
                        data-testid="save"
                      />
                      <Button
                        className="save_button"
                        as={Link}
                        to="/settings"
                        icon="cancel"
                        type="reset"
                        content={t("cancel", { ns: "common" })}
                      />
                    </Grid.Column>
                  </Grid.Row>
                  {checkApplication(Application.AutomotiveExpert) && (
                    <>
                      <Grid.Row className="removeBottomMargin"></Grid.Row>
                      <Grid.Row>
                        <PageHeader
                          header={t("tax.additionalTaxes", {
                            ns: "pricingSettings",
                          })}
                          type={"h2"}
                          divider={true}
                          addTitle={false}
                        />
                      </Grid.Row>
                      <Grid.Row>
                        <Grid.Column>
                          <Table>
                            <Table.Header>
                              <Table.HeaderCell textAlign="left"></Table.HeaderCell>
                              <Table.HeaderCell textAlign="left">
                                {t("tax_rates.taxName", {
                                  ns: "settings",
                                })}
                              </Table.HeaderCell>
                              <Table.HeaderCell textAlign="right">
                                {t("tax.rate", { ns: "pricingSettings" })}
                              </Table.HeaderCell>
                              <Table.HeaderCell textAlign="right">
                                {t("tax.maxRate", { ns: "pricingSettings" })}
                              </Table.HeaderCell>
                              <Table.HeaderCell>
                                {t("tax.apply2", { ns: "pricingSettings" })}
                              </Table.HeaderCell>
                              <Table.HeaderCell>
                                {t("tax.ignoreTaxExemptColumn", {
                                  ns: "pricingSettings",
                                })}
                              </Table.HeaderCell>
                              <Table.HeaderCell textAlign="right"></Table.HeaderCell>
                            </Table.Header>
                            <Table.Body>
                              {getTaxRates && getTaxRates.length > 0 ? (
                                getTaxRates.map((tr: SettingsTaxRatesDto) => {
                                  return (
                                    <Table.Row key={tr.id}>
                                      <Table.Cell>
                                        {" "}
                                        <Button
                                          color="grey"
                                          icon="edit"
                                          type="button"
                                          onClick={() => {
                                            setSelectedSettingsTaxRate(tr);
                                            modalStore.openModal(
                                              <UpsertTaxRate isAdd={false} />
                                            );
                                          }}
                                        ></Button>
                                      </Table.Cell>
                                      <Table.Cell textAlign="left">
                                        {tr.name}
                                      </Table.Cell>
                                      <Table.Cell textAlign="right">
                                        {tr.taxRate}
                                      </Table.Cell>
                                      <Table.Cell textAlign="right">
                                        {tr.maxTaxAmount
                                          ? tr.maxTaxAmount
                                          : null}
                                      </Table.Cell>
                                      <Table.Cell>
                                        {salesTaxOptionToString(
                                          tr.salesTaxOption
                                        )}
                                      </Table.Cell>
                                      <Table.Cell className="text-center">
                                        <Label
                                          basic
                                          color={
                                            tr.ignoreTaxExempt
                                              ? "green"
                                              : "grey"
                                          }
                                        >
                                          {tr.ignoreTaxExempt ? "Yes" : "No"}
                                        </Label>
                                      </Table.Cell>
                                      <Table.Cell textAlign="right">
                                        <Button
                                          color="grey"
                                          icon="trash"
                                          type="button"
                                          onClick={() =>
                                            modalStore.openModal(
                                              <ConfirmDelete
                                                header={t(
                                                  "selectableComments.remove",
                                                  {
                                                    ns: "settings",
                                                  }
                                                )}
                                                deleteValue={handleDelete}
                                                value={tr.id}
                                              />
                                            )
                                          }
                                        ></Button>
                                      </Table.Cell>
                                    </Table.Row>
                                  );
                                })
                              ) : (
                                <Table.Row key={"noitems"}>
                                  <Table.Cell width="8" colSpan={8}>
                                    <Message
                                      color="yellow"
                                      className="textAlignCenter fontStyleItalic"
                                    >
                                      {t("grid_noItems", { ns: "common" })}
                                    </Message>
                                  </Table.Cell>
                                </Table.Row>
                              )}
                            </Table.Body>
                            <Table.Footer>
                              <Table.Row>
                                <Table.HeaderCell colSpan="12">
                                  <MyPaging
                                    itemCount={
                                      pagination ? pagination.totalItems : 10
                                    }
                                    pageSize={pagingParams.pageSize}
                                    currentPage={
                                      pagination ? pagination.currentPage : 1
                                    }
                                    onClick={listOnClick}
                                    onChange={listOnClick}
                                    floated="right"
                                  ></MyPaging>
                                </Table.HeaderCell>
                              </Table.Row>
                            </Table.Footer>
                          </Table>
                        </Grid.Column>
                      </Grid.Row>
                      <Grid.Row>
                        <Grid.Column>
                          <Button
                            color={"green"}
                            className="save_button"
                            icon="plus"
                            type="button"
                            content={t("tax_rates.new", {
                              ns: "settings",
                            })}
                            onClick={() => {
                              modalStore.openModal(
                                <UpsertTaxRate isAdd={true} />
                              );
                            }}
                          />
                        </Grid.Column>
                      </Grid.Row>
                    </>
                  )}
                </>
              </Grid>
            </Form>
          )}
        </Formik>
      </Container>
    </>
  );
});
